import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

// The digest is off until somebody turns it on here. The switch lives in the
// database rather than in code, which is also what keeps the sandbox - same
// Gmail credentials, different database - from mailing the team.
export default function DigestSettings() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/digest");
      setCfg(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/digest", { ...cfg, ...patch });
      setCfg(data);
      toast.success(data.enabled ? "Daily digest is on" : "Daily digest is off");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async (when) => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/digest/send-now", { when });
      toast.success(`Sent to ${data.sent} admin${data.sent === 1 ? "" : "s"}`);
      (data.failed || []).forEach((f) =>
        toast.error(`${f.email}: ${f.error}`));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return null;
  const last = cfg.last_sent;

  return (
    <Card className="border-zinc-200 shadow-none">
      <CardHeader>
        <CardTitle className="text-base font-semibold">Daily dashboard digest</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-zinc-600">
          Emails the dashboard to every admin twice a day. The morning one lands
          after the late cut-off so the exceptions already mean something; the
          evening one after check-out, when hours and task logs are final.
        </p>

        <div className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2">
          <div>
            <div className="text-sm font-medium">
              {cfg.enabled ? "On" : "Off"}
            </div>
            <div className="text-[11px] text-zinc-500">
              {cfg.enabled
                ? `Sends at ${cfg.morning} and ${cfg.night} IST`
                : "Nothing is sent while this is off"}
            </div>
          </div>
          <Button size="sm" variant={cfg.enabled ? "outline" : "default"}
                  disabled={busy} data-testid="digest-toggle"
                  onClick={() => save({ enabled: !cfg.enabled })}>
            {cfg.enabled ? "Turn off" : "Turn on"}
          </Button>
        </div>

        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs text-zinc-600">Morning</label>
            <Input type="time" className="h-8 w-32" value={cfg.morning}
                   onChange={(e) => setCfg({ ...cfg, morning: e.target.value })} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-zinc-600">Evening</label>
            <Input type="time" className="h-8 w-32" value={cfg.night}
                   onChange={(e) => setCfg({ ...cfg, night: e.target.value })} />
          </div>
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => save({})}>Save times</Button>
        </div>

        <div>
          <div className="text-xs text-zinc-600 mb-1">
            Goes to {(cfg.recipients || []).length} admin
            {(cfg.recipients || []).length === 1 ? "" : "s"}
          </div>
          <div className="text-[11px] text-zinc-500">
            {(cfg.recipients || []).map((r) => r.email).join(", ") || "nobody"}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1 border-t border-zinc-100">
          <span className="text-xs text-zinc-600">Send one now:</span>
          <Button size="sm" variant="outline" className="h-7 text-xs"
                  disabled={busy} onClick={() => sendNow("morning")}>
            Morning
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs"
                  disabled={busy} onClick={() => sendNow("night")}>
            Evening
          </Button>
          {last && (
            <span className="text-[11px] text-zinc-400 ml-auto">
              last sent: {last.when} &middot; {last.sent} recipient
              {last.sent === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
